const FALLBACK_DISTINCT_ID_KEY = 'hb_mixpanel_distinct_id'

const getOrCreateFallbackDistinctId = () => {
  if (typeof window === 'undefined') return undefined
  try {
    const storage = window.localStorage
    if (!storage) return undefined
    let fallbackId = storage.getItem(FALLBACK_DISTINCT_ID_KEY)
    if (!fallbackId) {
      const randomId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      fallbackId = randomId
      storage.setItem(FALLBACK_DISTINCT_ID_KEY, fallbackId)
    }
    return fallbackId
  } catch {
    return undefined
  }
}

const sendToMixpanel = async (
  eventName: string,
  eventProperties?: Record<string, any>,
  userId?: string,
) => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.location?.pathname?.startsWith('/dashboard')
    ) {
      // Do not collect any analytics on internal dashboard routes
      return
    }
  } catch {}

  let effectiveUserId = userId
  // Try to include Mixpanel Session Replay properties if available
  let replayProperties: Record<string, any> = {}
  try {
    if (typeof window !== 'undefined') {
      const mp = (window as any).mixpanel
      if (mp && typeof mp.get_session_recording_properties === 'function') {
        replayProperties = mp.get_session_recording_properties() || {}
      }
      // If no explicit userId provided, fall back to mixpanel distinct_id when available
      if (!effectiveUserId && mp && typeof mp.get_distinct_id === 'function') {
        const distinctId = mp.get_distinct_id()
        if (distinctId) effectiveUserId = String(distinctId)
      }
    }
  } catch {}

  if (!effectiveUserId) {
    effectiveUserId = getOrCreateFallbackDistinctId()
  }
  // this part of code handles getting the UTM parameters that we can't get by default server side
  const urlParams = new URLSearchParams(window.location.search)
  const utmParams = {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    id: urlParams.get('id') || undefined,
  }

  // Here we are including additional data that will be sent to Mixpanel like device information, UTM parameters and location
  const additionalProperties = {
    distinct_id: effectiveUserId,
    $user_id: effectiveUserId,
    $browser: navigator.userAgent,
    $browser_version: navigator.appVersion,
    $current_url: window.location.href,
    $device: navigator.platform,
    $device_id: navigator.userAgent,
    $initial_referrer: document.referrer ? document.referrer : undefined,
    $initial_referring_domain: document.referrer
      ? new URL(document.referrer).hostname
      : undefined,
    $os: navigator.platform,
    $screen_height: window.screen.height,
    $screen_width: window.screen.width,
    ...replayProperties,
    ...utmParams,
  }
  const properties = {
    ...eventProperties,
    ...additionalProperties,
  }

  // Send to Mixpanel API (which will also handle Google Analytics via withUserTracking)
  const payload = {
    event: eventName,
    properties: properties,
  }

  // 开发环境打印调试信息
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 [Mixpanel] Sending event:', {
      event: eventName,
      properties,
      userId: effectiveUserId,
    })
  }

  fetch('/api/mixpanel', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (process.env.NODE_ENV === 'development') {
        if (response.ok) {
          console.log('✅ [Mixpanel] Event sent successfully:', eventName)
        } else {
          console.warn('⚠️ [Mixpanel] Event sent with status:', response.status)
        }
      }
      return response.json()
    })
    .catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [Mixpanel] Failed to send event:', error)
      }
    })
}

export default sendToMixpanel
